import React, { useEffect, useState, useCallback } from "react";
import { Formik, Form, useFormikContext } from "formik";
import { FormCheck, Button, Row, Col, FormLabel } from "react-bootstrap";
import PropTypes from "prop-types";
import FormInput from "./FormInput";
import PromptIfDirty from "./PromptIfDirty";
import { modifyFields } from "./DynamicForm";
import { useAuth } from "../../components/AuthContext/AuthContext";
import { useMediaQuery } from "react-responsive";

let medicationValue;
let diagnosedValue;
let supplementValue;

// ========================================
// COMPATIBILITY MAPPING
// ========================================

// Medical Conditions Mapping
const CONDITION_MAPPING = {
  "High cholesterol": null,
  "HIV": null,
  "Irritable bowel syndrome": "IBS",
  "Inflammatory bowel disease": "IBD",
  "Polycystic Ovary Syndrome": "PCOS",
  "COPD": null,
};

// Medications Mapping
const MEDICATION_MAPPING = {
  "Blood pressure medication": "Blood pressure",
  "Cholesterol lowering drugs": "Cholesterol-lowering",
  "Diabetes medication including insulin": "Diabetes (incl. insulin)",
  "Mental health medication": "Mental health",
  "Hormone replacement therapy": "HRT"
};

const mapPreviousToCurrent = (previousItems, mapping) => {
  if (!previousItems || !Array.isArray(previousItems)) {
    return [];
  }

  return previousItems
    .map(item => {
      if (mapping.hasOwnProperty(item)) {
        return mapping[item];
      }
      return item; 
    })
    .filter(item => item !== null && item !== undefined);
};

const getCompatibleValues = (values) => {
  const compatibleValues = { ...values };
  
  if (values.condition && Array.isArray(values.condition)) {
    compatibleValues.condition = mapPreviousToCurrent(values.condition, CONDITION_MAPPING);
  }

  if (values.medications && Array.isArray(values.medications)) {
    compatibleValues.medications = mapPreviousToCurrent(values.medications, MEDICATION_MAPPING);
  }
  
  return compatibleValues;
};

const CheckboxField = ({ checkboxField, values, initialValues, mapping }) => {
  const { setFieldValue } = useFormikContext();

  const handleChange = useCallback(
    (e) => {
      const { checked, value } = e.target;
      const name = checkboxField.name;
      const currentValues = values[name] || [];
      const updatedValues = checked
        ? [...currentValues, value]
        : currentValues.filter((v) => v !== value);

      setFieldValue(name, updatedValues);
    },
    [checkboxField.name, values, setFieldValue],
  );

  const isChecked = (values[checkboxField.name] || []).includes(checkboxField.value);

  return (

   <FormCheck
      type="checkbox"
      label={checkboxField.label}
      name={checkboxField.name}
      value={checkboxField.value}
      onChange={handleChange}
      checked={isChecked}
      disabled={checkboxField?.disabled ||checkboxField?.readOnly}
      className={`${checkboxField.className}`}
      aria-describedby={checkboxField.aria_describedby}
    />
  );
};
CheckboxField.propTypes = {
  checkboxField: PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    className: PropTypes.string,
    aria_describedby: PropTypes.string,
  }).isRequired,
  values: PropTypes.object.isRequired,
  initialValues: PropTypes.object,
  mapping: PropTypes.object,
};

const InputField = ({ field, values, touched, errors }) => {
  const { setFieldValue } = useFormikContext();

  const handleChange = useCallback(
    (e) => {
      setFieldValue(field.name, e.target.value);
    },
    [field.name, setFieldValue],
  );

  return (
    <FormInput
      id={`input-${field.controlId}`}
      key={field.controlId}
      className="form-group"
      type={field.type}
      {...field}
      name={field.name}
      value={values[field.name]}
      error={errors[field.name]}
      touched={touched[field.name]}
      onChange={handleChange}
      isInvalid={touched[field.name] && !!errors[field.name]}
      h1={field.h1}
    />
  );
};

InputField.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    controlId: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    h1: PropTypes.string.isRequired,
  }).isRequired,
  values: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
};

// Separate component for button
const ButtonField = ({ btn }) => {
  const auth = useAuth();

  const handleClick = useCallback(() => {
    if (btn.label === "Previous step") {
      auth.handlePreviousStep();
    } else if (btn.onClick) {
      btn.onClick();
    }
  }, [btn, auth]);

  return (
    <Button
      key={btn.controlId}
      type={btn.type}
      onClick={handleClick}
      size={btn.size}
      variant={btn.variant}
      className={btn.className}
    >
      {btn.label}
    </Button>
  );
};

ButtonField.propTypes = {
  btn: PropTypes.shape({
    controlId: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    size: PropTypes.string,
    variant: PropTypes.string,
    className: PropTypes.string,
  }).isRequired,
};

/**
 * A form component for displaying a checkbox group, used for medical conditions or medications.
 * This form allows users to select multiple options and submit the data, with dynamic field modification based on the user's selections.
 * The form is built using Formik for form handling and validation, and React Bootstrap for layout and styling.
 *
 * @prop {React.MutableRefObject} [formikRef] - Optional external ref to access the internal Formik
 *   instance (e.g. for calling validateForm() / values from a parent). When provided, the ref is
 *   assigned inside the Formik render prop so the parent can call
 *   `formikRef.current.validateForm()` and read `formikRef.current.values` just like DynamicForm.
 **/
const CheckboxGroupForm = ({
  fields,
  onSubmit,
  type,
  initialValues,
  blockRefresh,
  is_assessment_completed,
  formikRef,       // ← NEW: optional external ref
  hideBtnGroup,    // ← NEW: hide bottom nav buttons when embedded in Account accordion
  ...props
}) => {
  const auth = useAuth();
  const [formData, setFormData] = useState();
  const [title, setTitle] = useState();
  const [showCheckbox, setShowCheckbox] = useState();
  const [modifiedFields, setModifiedFields] = useState([]);
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const fieldArray = modifiedFields || fields;

  const getCompatibleInitialValues = useCallback(() => {
    const compatibleValues = getCompatibleValues(initialValues);
    
    const hasConditions = 
      (initialValues?.condition?.length > 0) || 
      (initialValues?.previous_conditions?.length > 0) ||
      (Array.isArray(initialValues?.condition) && initialValues.condition.some(c => 
        Object.keys(CONDITION_MAPPING).includes(c) || 
        !CONDITION_MAPPING[c]
      ));
    
    // Determine if medications were previously selected
    const hasMedications = 
      (initialValues?.medications?.length > 0) ||
      (initialValues?.previous_medications?.length > 0) ||
      (Array.isArray(initialValues?.medications) && initialValues.medications.some(m => 
        Object.keys(MEDICATION_MAPPING).includes(m) || 
        !MEDICATION_MAPPING[m]
      ));
    
    const hasSupplements = initialValues?.supplements?.length > 0;

    return {
      checked: compatibleValues.condition || compatibleValues.medications || compatibleValues.supplements || [],
      other_description: initialValues?.medical_details || "",
      medical_other_description: initialValues?.details || "",
      supplement_other_description: initialValues?.supplement_detail || "",
      was_diagnosed: (hasConditions && "Yes") || diagnosedValue || "No",
      medication: (hasMedications && "Yes") || medicationValue || "No",
      taking_supplements: (hasSupplements && "Yes") || supplementValue || "No",
      ...compatibleValues,
      _original_condition: initialValues?.condition || [],
      _original_medications: initialValues?.medications || [],
      _previous_conditions: initialValues?.previous_conditions || [],
      _previous_medications: initialValues?.previous_medications || [],
    };
  }, [initialValues]);

  useEffect(() => {
    if (is_assessment_completed) {
      const hasConditions = 
        initialValues?.condition?.length > 0 || 
        initialValues?.previous_conditions?.length > 0 ||
        (Array.isArray(initialValues?.condition) && initialValues.condition.some(c => 
          Object.keys(CONDITION_MAPPING).includes(c) || 
          !CONDITION_MAPPING[c]
        ));
      
      const hasMedications = 
        initialValues?.medications?.length > 0 ||
        initialValues?.previous_medications?.length > 0 ||
        (Array.isArray(initialValues?.medications) && initialValues.medications.some(m => 
          Object.keys(MEDICATION_MAPPING).includes(m) || 
          !MEDICATION_MAPPING[m]
        ));
      
      diagnosedValue = hasConditions ? "Yes" : "No";
      medicationValue = hasMedications ? "Yes" : "No";
      supplementValue = initialValues?.supplements?.length > 0 ? "Yes" : "No";
    } else {
      diagnosedValue = "No";
      medicationValue = "No";
      supplementValue = "No";
    }
  }, [initialValues, is_assessment_completed]);

  useEffect(() => {
    if (
      formData?.was_diagnosed === "Yes" ||
      formData?.medication === "Yes" ||
      formData?.taking_supplements === "Yes"
    ) {
      setTitle(true);
      setShowCheckbox(true);
      setModifiedFields(modifyFields(fields, formData, auth));
    } else if (
      formData?.was_diagnosed === "No" ||
      formData?.was_diagnosed === "Please choose" ||
      formData?.medication === "No" ||
      formData?.medication === "Please choose" ||
      formData?.taking_supplements === "No" ||
      formData?.taking_supplements === "Please choose"
    ) {
      setTitle(false);
      setShowCheckbox(false);
      setModifiedFields(fields);
    }
  }, [
    formData?.was_diagnosed,
    formData?.medication,
    formData?.taking_supplements,
    fields,
    auth,
    formData?.condition,
    formData?.medications,
    formData?.supplements,
  ]);

  const handleSubmit = useCallback(
    async (values) => {
      const medicalConditions = { ...values };
      const otherDetails = values.other_description;
      const MedicationOtherDetails = values.medical_other_description;
      const SupplimentOtherDetails = values.supplement_other_description;

      const formattedData = {
        medical_conditions: {
          conditions: medicalConditions,
          other_description: otherDetails,
        },
        medications: {
          medications: medicalConditions,
          other_description: MedicationOtherDetails,
        },
        supplements: {
          supplements: medicalConditions,
          other_description: SupplimentOtherDetails,
        },
      };
      
      if (values._original_condition && values._original_condition.length > 0) {
        formattedData.medical_conditions._mapped_from_conditions = values._original_condition;
      }
      
      if (values._original_medications && values._original_medications.length > 0) {
        formattedData.medications._mapped_from_medications = values._original_medications;
      }
      
      await onSubmit(formattedData);
    },
    [type, onSubmit],
  );

  const renderField = (field, values, errors, touched) => {
    if (field.type === "section-card" && Array.isArray(field.children)) {
      return (
        <React.Fragment key={field.controlId || field.title}>
          {field.title && (
            <h1 className="section-card-title mb-3">{field.title}</h1>
          )}

          <div className={field.className}>
            <div className="section-card-content">
              {field.children.map((child) =>
                renderField(child, values, errors, touched),
              )}
            </div>
          </div>
        </React.Fragment>
      );
    }
    if (Array.isArray(field?.check_box_group)) {
      let mapping = {};
      if (field.name === 'condition') {
        mapping = CONDITION_MAPPING;
      } else if (field.name === 'medications') {
        mapping = MEDICATION_MAPPING;
      }      
      return (
        <Row key={`row-${field.controlId}`}>
          {!field?.check_box_group[0]?.hide && (
            <div className="form-group m-0">
              <FormLabel className="labels form-label mt-2">
                Please tick all that apply:{" "}
                <span className="required"></span>
              </FormLabel>
            </div>
          )}

          {errors[field.name] && (
            <div className="invalid-feedback d-block mb-2">
              {errors[field.name]}
            </div>
          )}

          {showCheckbox &&
            field.check_box_group.map((checkboxField) => {
              if (checkboxField.type === "checkbox" && !checkboxField.hide) {
                return (
                  <Col md={4} key={checkboxField.controlId}>
                    <CheckboxField
                      checkboxField={checkboxField}
                      values={values}
                      initialValues={initialValues}
                      mapping={mapping}
                    />
                  </Col>
                );
              }

              return null;
            })}
        </Row>
      );
    }
    if (
      field.type !== "button" &&
      field.type !== "submit" &&
      !field.btnGroup &&
      field.type !== "group-label"
    ) {
      return (
        <InputField
          key={field.controlId}
          field={field}
          values={values}
          touched={touched}
          errors={errors}
        />
      );
    }
    if (!field.hide && field.type === "group-label") {
      return (
        <p className="mt-4" key={`label-${field.controlId}`}>
          {field.label}
        </p>
      );
    }

    return null;
  };

  return (
    <div>
      <Formik
        initialValues={getCompatibleInitialValues()}
        validationSchema={props.validationSchema}
        enableReinitialize={true}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={handleSubmit}
        innerRef={formikRef} // ← wires the external ref to Formik's instance
      >
        {({ values, errors, touched }) => {
          setFormData(values);
          return (
            <Form className="assessment-form" data-testid="form" noValidate>
              {blockRefresh && <PromptIfDirty />}
              <div className="container-fluid">
                <Row>
                  <Col md={12}>
                    {fieldArray?.map((field) =>
                      renderField(field, values, errors, touched),
                    )}
                  </Col>
                </Row>
              </div>

              {/* Hide navigation buttons when embedded inside Account accordion */}
              {!hideBtnGroup && (
                <div className={`d-flex flex-wrap justify-content-between align-items-center ${isMobile && "flex-column-reverse"} `}>
                  {fieldArray?.map((field) => {
                    if (field.btnGroup) {
                      return (
                        <React.Fragment key={`btn-group-${field.controlId}`}>
                          {field.btnGroup.map((btn) => (
                            <ButtonField key={btn.controlId} btn={btn} />
                          ))}
                        </React.Fragment>
                      );
                    }
                    return null;
                  })}
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

CheckboxGroupForm.propTypes = {
  fields: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  initialValues: PropTypes.object.isRequired,
  blockRefresh: PropTypes.bool,
  is_assessment_completed: PropTypes.bool,
  validationSchema: PropTypes.object,
  formikRef: PropTypes.shape({ current: PropTypes.any }),
  hideBtnGroup: PropTypes.bool,
};

CheckboxGroupForm.defaultProps = {
  hideBtnGroup: false,
};

export default CheckboxGroupForm;